'use client'

import { useCallback, useContext, useEffect, ReactNode } from 'react'
import { useMatch } from 'react-router-dom'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { INBOX_URL, CONVERSATION_URL } from '@marketplace-web/messaging/conversation-messaging-data'
import { ConversationListContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'

import { isBrazeConversation } from '../../utils'

type Props = {
  children?: ReactNode
}

const DocumentTitle = ({ children }: Props) => {
  const translate = useTranslate()

  const { inboxMessageCards } = useBrazeInboxMessageCards()
  const { conversations } = useContext(ConversationListContext)

  const conversationRouteMatch = useMatch(CONVERSATION_URL(':id'))
  const conversationListRouteMatch = useMatch(INBOX_URL)

  const setDocumentTitle = useCallback((title: string) => {
    if (document.title !== title) document.title = title
  }, [])

  useEffect(() => {
    if (!conversationListRouteMatch) return

    setDocumentTitle(translate('inbox.title'))
  }, [conversationListRouteMatch, setDocumentTitle, translate])

  useEffect(() => {
    const conversationId = conversationRouteMatch?.params.id
    const conversationInList = conversations.find(conv => conversationId === String(conv.id))
    const brazeConversationCard = inboxMessageCards?.find(card => card.id === conversationId)

    if (
      conversationInList &&
      !isBrazeConversation(conversationInList) &&
      'description' in conversationInList
    ) {
      setDocumentTitle(conversationInList.description)
    }

    if (brazeConversationCard) {
      setDocumentTitle(brazeConversationCard.title)
    }
  }, [conversationRouteMatch, inboxMessageCards, conversations, setDocumentTitle])

  // returning children leads to TS issues with no future-proof solution
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

export default DocumentTitle
